<template>
  <div
    class="group cursor-pointer flex flex-col w-full min-w-0 px-[12px] pt-[12px] pb-[14px] rounded-[32px] border border-solid border-[#dedede] dark:border-[#333333] hover:shadow-lg transition-all duration-300 overflow-hidden bg-white dark:bg-gray-900"
    @click="navigateToVehicle"
  >
    <!-- Vehicle Image Container -->
    <div class="relative bg-gray-100 dark:bg-gray-800 rounded-3xl overflow-hidden mb-6">
      <img
        v-if="vehicle.image || vehicle.thumb"
        :src="vehicle.image || vehicle.thumb"
        :alt="vehicle.title"
        class="w-full h-full object-cover"
        loading="lazy"
        @load="onImageLoad"
        @error="onImageError"
      >
      <div v-else class="w-full h-full flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-20 h-20 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
        </svg>
      </div>


      <!-- Sale Badge -->
      <div v-if="vehicle.was_price && vehicle.was_price > vehicle.price" class="absolute top-4 right-4">
        <div class="px-3 py-1 bg-red-500 text-white text-xs font-medium rounded-full">
          Sale
        </div>
      </div>

      <!-- Hover Action Button -->
      <div class="absolute inset-0 bg-black/0 group-hover:bg-black/5 dark:group-hover:bg-white/5 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
        <button
          class="h-11 w-11 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-white rounded-full p-3 shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-all duration-300"
          @click.stop="openQuickView"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Vehicle Information -->
    <div class="flex-1 flex flex-col space-y-2">
      <!-- Vehicle Content (grows to fill space) -->
      <div class="flex-1 space-y-2">
        <!-- Dealer Name -->
        <div v-if="vehicle.dealer?.displayValue?.[0]" class="text-sm text-gray-600 dark:text-gray-400 font-medium">
          {{ vehicle.dealer.displayValue[0] }}
        </div>

        <!-- Vehicle Title -->
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
          {{ vehicle.title || 'Unknown Vehicle' }}
        </h3>

        <!-- Vehicle Details -->
        <div class="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <div class="flex items-center gap-3">
            <span v-if="vehicle.year?.displayValue?.[0]">{{ vehicle.year.displayValue[0] }}</span>
            <span v-if="vehicle.kms">{{ formatKilometers(vehicle.kms) }}</span>
            <span v-if="vehicle.transmission?.displayValue?.[0]">{{ vehicle.transmission.displayValue[0] }}</span>
          </div>
        </div>

        <!-- Price Section -->
        <div class="flex items-center justify-between pt-2">
          <div class="flex flex-col">
            <!-- Current Price -->
            <span class="text-xl font-semibold text-gray-900 dark:text-white">
              {{ formatPrice(vehicle.price) }}
            </span>
            <div class="text-sm text-gray-600 dark:text-gray-400">
              DRIVE AWAY
            </div>
            <!-- Weekly Payment -->
            <!-- <span v-if="vehicle.perweek || vehicle.price" class="text-sm text-gray-600 dark:text-gray-400">
              ${{ getWeeklyPayment(vehicle.perweek, vehicle.price) }}/week
            </span> -->
          </div>


          <!-- Fuel Economy or Rating -->
          <div v-if="vehicle.fuel_economy_combined" class="flex items-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ vehicle.fuel_economy_combined }}L/100km
            </span>
          </div>
        </div>

        <!-- Tags (minimalist) -->
        <div v-if="getVehicleTags().length > 0" class="flex flex-wrap gap-1 pt-1">
          <span
            v-for="tag in getVehicleTags().slice(0, 2)"
            :key="tag"
            class="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-md"
          >
            {{ tag }}
          </span>
        </div>
      </div>

      <!-- Action Buttons (pinned to bottom) -->
      <div class="flex gap-2 pt-3">
        <button
          class="flex-1 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white py-3 px-4 rounded-full font-medium transition-colors duration-200"
          @click.stop="navigateToVehicle"
        >
          View Details
        </button>

        <button
          class="flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full transition-colors duration-200"
          @click.stop="toggleFavorite"
          :title="isFavorite ? 'Remove from favorites' : 'Add to favorites'"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            class="w-4 h-4 transition-colors duration-200"
            :class="isFavorite ? 'text-red-500 fill-current' : ''"
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>

        <button
          class="flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full transition-colors duration-200"
          @click.stop="addToCart"
          :title="isInCart ? 'Remove from cart' : 'Add to cart'"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            class="w-4 h-4 transition-colors duration-200"
            :class="isInCart ? 'text-blue-600 dark:text-blue-400' : ''"
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVehicleQuickView } from '~/layers/base/composables/useVehicleQuickView';
import { useRouter } from '#imports';
import { useFavoritesStore } from '~/stores/favorites';
import { useCartStore } from '~/stores/cartStore';
import { computed, ref, onMounted } from 'vue';
import { calculateWeeklyPayment } from '~/layers/base/utils/finance';

const { openQuickView: openQuickViewModal } = useVehicleQuickView();
const router = useRouter();
const favoritesStore = useFavoritesStore();
const cartStore = useCartStore();

const props = defineProps({
  vehicle: {
    type: Object,
    required: true,
    default: () => ({
      id: '',
      slug: '',
      title: '',
      description: '',
      image: '',
      year: null,
      mileage: null,
      price: null,
      perweek: null,
      fuel: null
    })
  },
  layout: {
    type: String,
    default: 'grid',
    validator: (value: string) => ['grid', 'list', 'compact'].includes(value)
  }
});

// Image loading state
const imageLoaded = ref(false);
const imageError = ref(false);

// Handle image load event
const onImageLoad = () => {
  imageLoaded.value = true;
};

// Handle image error event
const onImageError = () => {
  imageError.value = true;
  imageLoaded.value = true;
};

// Check if image is already cached on mount
onMounted(() => {
  if (props.vehicle.thumb || props.vehicle.image) {
    const img = new Image();
    img.onload = onImageLoad;
    img.onerror = onImageError;
    img.src = props.vehicle.image || props.vehicle.thumb;

    setTimeout(() => {
      if (img.complete) {
        onImageLoad();
      }
    }, 50);
  }
});

// Computed properties
const isFavorite = computed(() => {
  return favoritesStore.isFavorite(props.vehicle.id);
});

const isInCart = computed(() => {
  return cartStore.isInCart(props.vehicle.id);
});

// Methods
const formatPrice = (price: number): string => {
  if (!price) return 'Contact for pricing';
  
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

const formatKilometers = (kms: number): string => {
  if (!kms) return '';
  return `${kms.toLocaleString()}km`;
};

const getWeeklyPayment = (perweek: any, price: number): string => {
  const weeklyValue = Number(perweek ?? 0);
  if (weeklyValue > 0) return weeklyValue.toLocaleString();

  if (price > 0) {
    const calculated = calculateWeeklyPayment(price);
    return calculated > 0 ? calculated.toLocaleString() : '0';
  }

  return Math.round(price / 52).toLocaleString();
};

const getVehicleTags = (): string[] => {
  const tags = [];
  
  if (props.vehicle.fuel?.displayValue?.[0]) {
    tags.push(props.vehicle.fuel.displayValue[0]);
  }
  
  if (props.vehicle.body?.displayValue?.[0]) {
    tags.push(props.vehicle.body.displayValue[0]);
  }
  
  if (props.vehicle.drivetrain?.displayValue?.[0]) {
    tags.push(props.vehicle.drivetrain.displayValue[0]);
  }
  
  return tags;
};

const navigateToVehicle = () => {
  const vehicle = props.vehicle;
  const slug = vehicle.slug || getVehicleSlug(vehicle);
  router.push(`/vehicle-for-sale/${vehicle.stockid || vehicle.id}/${slug}`);
};

const openQuickView = () => {
  openQuickViewModal(props.vehicle.id);
};

const toggleFavorite = () => {
  favoritesStore.toggleFavorite(props.vehicle.id);
};

const addToCart = () => {
  cartStore.addToCart(props.vehicle.id);
};

// Helper to generate a slug if one doesn't exist
const getVehicleSlug = (vehicle: any) => {
  const title = vehicle.title ||
    (vehicle.make?.displayValue?.[0] && vehicle.model?.displayValue?.[0] ?
      `${vehicle.make.displayValue[0]} ${vehicle.model.displayValue[0]}` :
      'Unknown Vehicle');

  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Smooth transitions for all interactive elements */
* {
  transition: color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

/* Ensure proper image scaling */
img {
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}

/* Enhanced hover effects */
.group:hover img {
  transform: scale(1.05);
  transition: transform 0.5s ease;
}

@media (max-width: 768px) {
  /* Responsive adjustments for mobile */
  .group {
    margin-bottom: 2rem;
  }
}
</style>